import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  OutlinedInput,
  Snackbar,
  Typography,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import { appLogger, Http } from "../../../../src/utils";
import { UserContext } from "../../../context/userContext";
import { UserLoginInfo } from "../../../types/context";
import {
  IProductParams,
  PLATFORM_TYPE_BROWSER,
  PLATFORM_TYPE_MOBILE,
  PLATFORM_TYPE_TV,
  webTechs,
  mobTechs,
  tvTechs,
  IPlatformType,
  PlatformTypesList,
} from "../../../model";
import { hostUrl } from "../../../utils/http";
import { withSnackbar } from "../../../utils/common/SnackbarHoc";
import HelpIcon from "@mui/icons-material/Help";
import {
  Download,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  OpenInNew,
} from "@mui/icons-material";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { HideOrShowText } from "../../common/hideOrShowText";

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, .05)"
      : "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

const widgetScript = `<script>
  window.Cuvo = window.Cuvo  || {};
  Cuvo.ready = false;
  Cuvo.apiKey = \'YOUR_PRODUCT_API_KEY_GOES_HERE\';
  Cuvo.orgId = \'YOUR_ORGID_GOES_HERE\';
  Cuvo.prodId = \'YOUR_PRODUCT_ID_GOES_HERE\';
  Cuvo.prodVer = \'YOUR_PRODUCT_VERSION_GOES_HERE\';
  Cuvo.endpoint = \'${hostUrl}\';
  (function(d) {
    var s = d.createElement(\'script\'); s.async = true;
    s.src = \'${window.location.origin}/dist/feedback-agent/browser/cuvo_script.js\';
    (d.head || d.body).appendChild(s);
  })(document);
</script>`;

const widgetScript2 = `<script  dangerouslySetInnerHTML={{ __html: ${
  "`" +
  `window.Cuvo = window.Cuvo  || {};
  Cuvo.ready = false;
  Cuvo.apiKey = \'YOUR_PRODUCT_API_KEY_GOES_HERE\';
  Cuvo.orgId = \'YOUR_ORGID_GOES_HERE\';
  Cuvo.prodId = \'YOUR_PRODUCT_ID_GOES_HERE\';
  Cuvo.prodVer = \'YOUR_PRODUCT_VERSION_GOES_HERE\';
  Cuvo.endpoint = \'${hostUrl}\';
  (function(d) {
    var s = d.createElement(\'script\'); s.async = true;
    s.src = \'${window.location.origin}/dist/feedback-agent/browser/cuvo_script.js\';
    (d.head || d.body).appendChild(s);
  })(document);` +
  "`"
}}}>
</script>`;
const reactCode = `import Cuvo from '@cuvo/cuvo-react-native';
<Cuvo
    isDarkTheme={false}
    isCustomButton={false}
    orgId = \'YOUR_ORGID_GOES_HERE\'
    prodId=\'YOUR_PRODUCT_ID_GOES_HERE\'
    productVersion=\'YOUR_PRODUCT_VERSION_GOES_HERE\'
    endPoint={\'${hostUrl}\'}
    apiKey=\'YOUR_PRODUCT_API_KEY_GOES_HERE\'
/>`;

const npmCode = `//registry.npmjs.org/:_authToken=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
`;

const reactTVCode = `import Cuvo from '@cuvo/cuvotv-react-native';
<Cuvo
    isDarkTheme={false}
    isCustomButton={false}
    orgId = \'YOUR_ORGID_GOES_HERE\'
    prodId=\'YOUR_PRODUCT_ID_GOES_HERE\'
    productVersion=\'YOUR_PRODUCT_VERSION_GOES_HERE\'
    endPoint={\'${hostUrl}\'}
    apiKey=\'YOUR_PRODUCT_API_KEY_GOES_HERE\'
/>`;

const kotlinTVCode = `
import com.example.cuvotvkotlinlibrary.CuvoInitConfigs;

var productDetails = JSONObject()
productDetails.put("endPoint", \'${hostUrl}\')
productDetails.put("productVersion", \'YOUR_PRODUCT_VERSION_GOES_HERE\')
productDetails.put("emailId", "Your email id")
productDetails.put("prodId", \'YOUR_PRODUCT_ID_GOES_HERE\')
productDetails.put("orgId", \'YOUR_ORGID_GOES_HERE\')
productDetails.put("apiKey", \'YOUR_PRODUCT_API_KEY_GOES_HERE\')
productDetails.put("isDarkMode", false);
CuvoInitConfigs.createCuvoActivity(productDetails)
`;

const javaTVCode = `  
import com.example.cuvotvkotlinlibrary.CuvoInitConfigs;

JSONObject productDetails = new JSONObject();
try {
    productDetails.put("endPoint",\'${hostUrl}\'
    );
    productDetails.put("productVersion", \'YOUR_PRODUCT_VERSION_GOES_HERE\'");
    productDetails.put("emailId", "Your email Id");
    productDetails.put("prodId", \'YOUR_PRODUCT_ID_GOES_HERE\');
    productDetails.put("orgId", \'YOUR_ORGID_GOES_HERE\');
    productDetails.put("apiKey", \'YOUR_PRODUCT_API_KEY_GOES_HERE\');
    productDetails.put("isDarkMode", false);
} catch (JSONException e) {
    e.printStackTrace();
}
CuvoInitConfigs.Companion.createCuvoActivity(productDetails);`;

const rokuTvCode = `
  function cuvoSDKLoaded()
    if (m.cuvoSDK.loadStatus = "ready")
      m.cuvoAgent = CreateObject("RoSGNode","CuvoComponentLib:cuvo")
      m.cuvoAgent.apiKey=\"YOUR_PRODUCT_API_KEY_GOES_HERE\"
      m.cuvoAgent.orgId = \"YOUR_ORGID_GOES_HERE\"
      m.cuvoAgent.prodId=\"YOUR_PRODUCT_ID_GOES_HERE\"
      m.cuvoAgent.productVersion=\"YOUR_PRODUCT_VERSION_GOES_HERE\"
      m.cuvoAgent.endPoint=\"${hostUrl}\"
      m.cuvoAgent.regularFont = "pkg:/components/cuvoAgent/fonts/poppins/Poppins-Medium.ttf"
      m.cuvoAgent.boldFont = "pkg:/components/cuvoAgent/fonts/poppins/Poppins-Bold.ttf"
      m.cuvoAgent.observeField("close", "closeCuvoFeedbackAgent")
      m.cuvoContainer.appendChild(m.cuvoAgent)
    end if
end function
`;

const htlm5Script = `<script>
  window.Cuvo = window.Cuvo  || {};
  Cuvo.ready = false;
  Cuvo.apiKey = \'YOUR_PRODUCT_API_KEY_GOES_HERE\';
  Cuvo.orgId = \'YOUR_ORGID_GOES_HERE\';
  Cuvo.prodId = \'YOUR_PRODUCT_ID_GOES_HERE\';
  Cuvo.prodVer = \'YOUR_PRODUCT_VERSION_GOES_HERE\';
  Cuvo.endpoint = \'${hostUrl}\';
  (function(d) {
    var s = d.createElement(\'script\'); s.async = true;
    s.src = \'${window.location.origin}/dist/feedback-agent/tv/hostedwebapp/cuvo_script.js
    \';
    (d.head || d.body).appendChild(s);
  })(document);
</script>`;

function APISettings(props: any) {
  appLogger.info({
    infoObj: {
      method: "APISettings",
      component: "add-product",
      fileName: "apiSettings.tsx",
      props: props,
    },
  });
  // const [selectedImage, setSelectedImage] = useState<any | null>(null);
  const {
    productParams,
    setProductParams,
    setScroll,
    sethandleClick,
    enableJira,
  } = props;
  // const [imageUrl, setImageUrl] = useState<any | null>(null);
  // const [feedback, setFeedback] = useState("");
  // const [xmlCode, setXmlCode] = useState<any | null>(null);
  // const [base64, setBase64] = useState<any | null>(null);
  // const [generalComment, setGeneralComment] = useState(false);
  const {
    userInfo,
    setBasicDetailAccordianContext,
    setGenSettingsAccordianContext,
    setFbSettingsAccordianContext,
    setBugSettingsAccordianContext,
    setFreqSettingsAccordianContext,
    EmailSettingsAccordianContext,
    setEmailSettingsAccordianContext,
    setExttrackingSettingsAccordianContext,
    ExttrackingSettingsAccordianContext,
    setWidgAppSettingsAccordianContext,
    WidgAppSettingsAccordianContext,
    WidgIntgSettingsAccordianContext,
    setWidgIntgSettingsAccordianContext,
    setQRSettingsAccordianContext,
  } = useContext(UserContext) as UserLoginInfo;
  // const [showEmail, setShowEmail] = useState(false);
  // const [desc, setDesc] = useState(widgetScript);

  // const scriptHelpText: string = `Refer the Web Browser integration document for integration details.`;

  // const reactHelpText: string = `Refer the React Native integration document for integration details.`;
  const [isLoading, setIsLoading] = useState(false);
  const [openResetApiKeyDialog, setOpenResetApiKeyDialog] = useState(false);
  const [selectedTech, setTechnology] = React.useState("");

  useEffect(() => {
    if (
      productParams &&
      productParams.products[0] &&
      productParams.products[0].technology
    ) {
      setTechnology(productParams.products[0].technology);
    }
  });

  const copyToClipboard = (textAreaId: string) => {
    const textArea = document.querySelector(`#${textAreaId}`);
    const textToCopy =
      textArea && textArea.textContent ? textArea.textContent : "";
    navigator.clipboard.writeText(textToCopy);
    props.snackbarShowMessage("Script copied to clipboard", "success");
  };

  // const handleResetPassword = (email: string) => {
  //   setOpenResetPwDialog(true);
  //   setFocusEmailId(email);
  // }

  const handleCloseResetApiKeyDialog = () => {
    setOpenResetApiKeyDialog(false);
  };

  useEffect(() => {
    if (
      !productParams.products[0].technology ||
      (productParams &&
        productParams.products &&
        productParams.products[0] &&
        productParams.products[0].technology === "")
    ) {
      if (
        productParams &&
        productParams.products &&
        productParams.products[0] &&
        productParams.products[0].platform === PLATFORM_TYPE_BROWSER
      ) {
        setTechnology(webTechs[0].value);
      } else if (
        productParams &&
        productParams.products &&
        productParams.products[0] &&
        productParams.products[0].platform === PLATFORM_TYPE_MOBILE
      ) {
        setTechnology(mobTechs[0].value);
      } else if (
        productParams &&
        productParams.products &&
        productParams.products[0] &&
        productParams.products[0].platform === PLATFORM_TYPE_TV
      ) {
        setTechnology(tvTechs[0].value);
      }
    } else if (
      productParams &&
      productParams.products &&
      productParams.products[0] &&
      productParams.products[0].technology
    ) {
      setTechnology(productParams.products[0].technology);
    } else {
      setTechnology("vanilla");
    }

    // if (
    //   productParams &&
    //   productParams.products &&
    //   productParams.products[0] &&
    //   !productParams.products[0].apiKey
    // ) {
    //   regenerateApiKey(
    //     "",
    //     productParams.products[0].id,
    //     productParams.products[0].version
    //   );
    // }
  }, [productParams]);

  const handleApiKey = (val: any) => {
    appLogger.info({
      infoObj: {
        method: "handleApiKey",
        component: "add-product",
        fileName: "apiSettings.tsx",
        apiKeyValue: val,
      },
    });
    if (productParams) {
      const temp: IProductParams | undefined = { ...productParams };
      if (temp && temp.products && temp.products[0]) {
        temp.products[0].apiKey = val.apiKey;
        temp.products[0].apiKeyId = val.apiKeyId;
        setProductParams(temp);
      }
    }
  };
  let codeSnippet = "";
  // let instructions = '';
  // let helpText = '';

  const regenerateApiKey = (
    apiKeyId: string,
    prodId: string,
    version: string
  ) => {
    if (prodId) {
      setIsLoading(true);
      appLogger.info({
        infoObj: {
          method: "regenerateApiKey",
          component: "add-product",
          fileName: "apiSettings.tsx",
          regenerateApiKeyData: { apiKeyId, prodId, version },
        },
      });
      Http.post({
        url: `/api/v2/productApiKey/`,
        state: userInfo,
        body: {
          apiKeyId: apiKeyId,
          productId: prodId,
          version: version,
        },
      })
        .then((response: any) => {
          appLogger.info({
            infoObj: {
              method: "regenerateApiKey",
              component: "add-product",
              fileName: "apiSettings.tsx",
              productData: response,
            },
          });
          handleApiKey(response);
          setIsLoading(false);
          props.snackbarShowMessage(
            "Generated new apikey successfully",
            "success",
            3000
          );
          setOpenResetApiKeyDialog(false);
        })
        .catch((error) => {
          appLogger.error({
            errObj: {
              method: "regenerateApiKey",
              component: "add-product",
              fileName: "apiSettings.tsx",
              err: error,
            },
          });
          setIsLoading(false);
        });
    }
  };
  // let techDDdatas: any = [];
  if (
    productParams &&
    productParams.products &&
    productParams.products[0] &&
    productParams.products[0].platform &&
    productParams.products[0].platform === PLATFORM_TYPE_MOBILE
  ) {
    // instructions = 'Copy the below code and paste it in the main App component file.' +
    //   'Check detailed help for package installation, configuration and other intergration options.';
    codeSnippet = reactCode;
    // techDDdatas = mobTechs;
    // helpText = reactHelpText;
  } else {
    if (
      productParams &&
      productParams.products &&
      productParams.products[0] &&
      productParams.products[0].platform &&
      productParams.products[0].platform === PLATFORM_TYPE_BROWSER
    ) {
      codeSnippet = widgetScript;
      // techDDdatas = webTechs;
    } else {
      if (selectedTech == "androidKotlinTv") {
        codeSnippet = kotlinTVCode;
        // techDDdatas = tvTechs;
      } else if (selectedTech == "androidJavaTv") {
        codeSnippet = javaTVCode;
        // techDDdatas = tvTechs;
      } else if (selectedTech == "rokuTv") {
        codeSnippet = rokuTvCode;
      } else if (selectedTech == "html5Tv") {
        codeSnippet = htlm5Script;
      } else {
        codeSnippet = reactTVCode;
        // techDDdatas = tvTechs;
      }
    }
    // codeSnippet = widgetScript;
    // instructions = 'Copy the below script and paste it in the head tag of your html page.' +
    //   'Check detailed help on other intergration options.';
    if (
      selectedTech == "react" ||
      selectedTech == "angular" ||
      selectedTech == "vue" ||
      selectedTech == "jquery" ||
      selectedTech == "vanilla"
    ) {
      codeSnippet = widgetScript;
    } else if (selectedTech == "nextjs" || selectedTech == "preact") {
      codeSnippet = widgetScript2;
      // helpText = scriptHelpText;
    }
  }

  const scroll = (e: any) => {
    e.stopPropagation();
    setScroll("widgetCode");
    handleAccordianChange(true, "basicDetails", "accordian");
    sethandleClick(2);
  };
  const handleAccordianChange = (value: any, type: any, clickType: any) => {
    appLogger.info({
      infoObj: {
        method: "handleAccordianChange",
        component: "add-product",
        fileName: "apiSettings.tsx",
        accordianData: { value, type },
      },
    });
    setGenSettingsAccordianContext(false);
    setBasicDetailAccordianContext(false);
    setFbSettingsAccordianContext(false);
    setBugSettingsAccordianContext(false);
    setFreqSettingsAccordianContext(false);
    setEmailSettingsAccordianContext(false);
    setExttrackingSettingsAccordianContext(false);
    setWidgAppSettingsAccordianContext(false);
    setWidgIntgSettingsAccordianContext(value);
    setQRSettingsAccordianContext(false);
    // if (clickType == "next") {
    //   setWidgIntgSettingsAccordianContext(true);
    //   setWidgAppSettingsAccordianCovscode-file://vscode-app/c:/Users/Harithaa/AppData/Local/Programs/Microsoft%20VS%20Code/resources/app/out/vs/code/electron-sandbox/workbench/workbench.htmlntext(false);
    // }
    if (clickType == "back") {
      setWidgAppSettingsAccordianContext(true);
      setWidgIntgSettingsAccordianContext(false);
    }
  };
  const technologyContent = (val: any) => {
    let content =
      "Below is your code snippet required for integration. Add this script tag as a child of the head tag in your main index.html file. Refer the instructions given in the right hand side help area for more details.";
    if (val == "react" || val == "angular" || val == "vue") {
      content =
        "Below is your code snippet required for integration. Add this script tag as a child of the head tag in your main index.html file. Refer the instructions given in the right hand side help area for more details.";
    } else if (val == "vanilla" || val == "jquery") {
      content =
        "Below is your code snippet required for integration. Add this script tag as a child of the head tag in all the files that has the head tag. Refer the instructions given in the right hand side help area for more details.";
    } else if (val == "nextjs" || val == "preact") {
      content =
        "Below is your code snippet required for integration. Add this script tag as a child of the head tag in your  index.js or _document.js file. Refer the instructions given in the right hand side help area for more details.";
    } else if (val == "androidKotlinTv" || val == "androidJavaTv") {
      content =
        "Below is your code snippet required for integration. Add these line in the main acitivity file. Refer the integration document for more details.";
    } else if (val == "reactNative" || val == "reactNativeTv") {
      content =
        "Below is your code snippet required for integration. Add these line in the App.js file";
    }
    return content;
  };

  const renderPreviuosBtnToolTip = () => {
    let tooltip: any = "";
    tooltip = "Go to Widget Appereance";
    return tooltip;
  };

  const selectPlatform = (event: SelectChangeEvent) => {
    if (productParams) {
      const temp: IProductParams | undefined = { ...productParams };

      if (temp && temp.products[0]) {
        temp.products[0].platform = event.target.value as IPlatformType;
        setProductParams(temp);
      }
    }
  };

  let techDDdatas: any = [];
  if (productParams?.products?.[0]?.platform === PLATFORM_TYPE_MOBILE) {
    techDDdatas = mobTechs;
  } else if (productParams?.products?.[0]?.platform === PLATFORM_TYPE_TV) {
    techDDdatas = tvTechs;
  } else {
    techDDdatas = webTechs;
  }

  const handleTechnologyChange = (event: any) => {
    setTechnology(event.target.value);
    if (productParams) {
      const temp: IProductParams | undefined = { ...productParams };

      if (temp && temp.products[0]) {
        temp.products[0].technology = event.target.value;
        setProductParams(temp);
      }
    }
  };

  return (
    <>
      <Dialog
        className="reset-apiKey-form form-container text-center"
        onClose={handleCloseResetApiKeyDialog}
        open={openResetApiKeyDialog}
      >
        <Box
          className={isLoading ? "loader" : "form-wrap"}
          style={
            isLoading
              ? { height: "250px", paddingLeft: "24px", paddingRight: "24px" }
              : { paddingLeft: "24px", paddingRight: "24px" }
          }
        >
          <DialogTitle>Reset Api-Key</DialogTitle>
          <Typography className="info">
            Do you really want to reset apiKey for{" "}
            {productParams &&
            productParams.products &&
            productParams.products[0].name
              ? productParams.products[0].name
              : ""}
            ?
          </Typography>
          <Box style={{ paddingBottom: "16px", paddingTop: "16px" }}>
            <Button
              variant="contained"
              style={{ backgroundColor: "#ff6969" }}
              onClick={(event: any) =>
                regenerateApiKey(
                  productParams.products[0].apiKeyId,
                  productParams.products[0].id,
                  productParams.products[0].version
                )
              }
            >
              reset
            </Button>
            <Button
              variant="text"
              onClick={handleCloseResetApiKeyDialog}
              style={{ marginLeft: "10px" }}
            >
              cancel
            </Button>
          </Box>
        </Box>
      </Dialog>
      <Accordion
        expanded={WidgIntgSettingsAccordianContext}
        onChange={(e, expanded) => {
          if (expanded) {
            handleAccordianChange(expanded, "basicDetails", "accordian");
          } else {
            handleAccordianChange(expanded, "basicDetails", "accordian");
          }
        }}
      >
        <AccordionSummary aria-controls={"panel1-content"} id={"panel1-header"}>
          Integration steps
          <Tooltip title="Integration steps Help" placement="bottom">
            <HelpIcon sx={{ mx: 1, fontSize: "20px" }} onClick={scroll} />
          </Tooltip>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2} className={isLoading ? "loader" : ""}>
            {productParams &&
            productParams.products &&
            productParams.products[0] &&
            productParams.products[0].apiKey ? (
              <Grid item xs={12} md={12} sx={{ mt: 2 }} className="apiKey-wrap">
                <Grid container spacing={2} sx={{ alignItems: "center" }}>
                  <Grid item sx={{ flexGrow: 1 }}>
                    <HideOrShowText
                      placeHolder={"API key"}
                      value={
                        productParams.products[0].apiKey
                          ? productParams.products[0].apiKey
                          : ""
                      }
                      fieldKey={"prod-apiKey"}
                      disabled
                    />
                  </Grid>
                  <Grid item>
                    <>
                      <Tooltip title="Reset ApiKey" placement="top">
                        <RestartAltOutlinedIcon
                          className="regenerate-apiKey"
                          onClick={(event: any) =>
                            setOpenResetApiKeyDialog(true)
                          }
                        />
                      </Tooltip>
                    </>
                  </Grid>
                </Grid>
              </Grid>
            ) : (
              <Grid item md={12} xs={8} sx={{ pt: 0 }}>
                {!productParams.apikey && (
                  <Stack sx={{ width: "100%" }} pt={2}>
                    <Alert severity="info">
                      Please generate API key to view the integration steps
                    </Alert>
                  </Stack>
                )}
                <Button
                  variant="contained"
                  onClick={(event: any) =>
                    regenerateApiKey(
                      "",
                      productParams.products[0].id,
                      productParams.products[0].version
                    )
                  }
                >
                  generate API key
                </Button>
              </Grid>
            )}

            {productParams?.products?.[0]?.apiKey && (
              <Grid item xs={12} md={6}>
                <FormControl
                  className="custom-select gudieTourPlatform"
                  fullWidth
                >
                  <InputLabel>Platform</InputLabel>
                  <Select
                    labelId="platform-select"
                    id="platform-select"
                    value={
                      productParams?.products?.[0]?.platform
                        ? productParams?.products?.[0]?.platform
                        : PLATFORM_TYPE_BROWSER
                    }
                    label="platform"
                    onChange={selectPlatform}
                  >
                    {PlatformTypesList.map((e: any, i: any) => (
                      <MenuItem key={e.value} value={e.value}>
                        {e.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {productParams?.products?.[0]?.apiKey && (
              <Grid item xs={12} md={6}>
                <FormControl
                  className="custom-select guideTourTechnology"
                  fullWidth
                >
                  <InputLabel>Technology</InputLabel>
                  <Select
                    labelId="technology-select"
                    id="technology-select"
                    value={
                      productParams?.products?.[0]?.technology
                        ? productParams?.products?.[0]?.technology
                        : selectedTech
                    }
                    label="Technology"
                    onChange={(event) => handleTechnologyChange(event)}
                  >
                    {techDDdatas.map((e: any, i: any) => (
                      <MenuItem value={e.value}>
                        {" "}
                        <img className="technology-icons" src={e.logo} />
                        {e.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {productParams?.products?.[0]?.apiKey && (
              <Grid item xs={12} sx={{ mt: 1.5 }}>
                <Typography className="title">
                  Agent Integration Details
                </Typography>
                <Typography className="label" sx={{ mt: 2, mb: 1 }}>
                  {technologyContent(selectedTech)}
                </Typography>
                {selectedTech == "androidKotlinTv" ||
                selectedTech == "androidJavaTv" ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(event: any) =>
                      window.open(
                        `${window.location.origin}/docs/installation-guide/tv/kotlin/kotlin-agent-integration-doc.pdf`,
                        "_blank"
                      )
                    }
                  >
                    <OpenInNew />
                    <span>Go To Integration Doc</span>
                  </Button>
                ) : null}
                {selectedTech == "androidKotlinTv" ||
                selectedTech == "androidJavaTv" ? (
                  <Button
                    style={{ marginLeft: "10px" }}
                    size="small"
                    variant="outlined"
                    onClick={(event: any) =>
                      (window.location.href = `${window.location.origin}/dist/feedback-agent/tv/kotlin/cuvotvkotlinlibrary-release.aar"`)
                    }
                  >
                    <Download />
                    <span>Download SDK</span>
                  </Button>
                ) : null}
                {selectedTech == "reactNative" ||
                selectedTech == "reactNativeTv" ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(event: any) =>
                      (window.location.href = `${window.location.origin}/docs/installation-guide/tv/react-native/CuVo_React_Native_Agent_Integration_Document.docx`)
                    }
                  >
                    <Download />
                    <span>Download Integration Doc</span>
                  </Button>
                ) : null}
                {selectedTech == "rokuTv" ? (
                  <Button
                    style={{ marginLeft: "10px" }}
                    size="small"
                    variant="outlined"
                    // href="#"
                    // target="_blank"
                    onClick={(event: any) =>
                      window.open(
                        `${window.location.origin}/docs/installation-guide/tv/roku/roku-cuvo-sdk-integration.pdf`,
                        "_blank"
                      )
                    }
                  >
                    <OpenInNew />
                    <span>Go To Integration Doc</span>
                  </Button>
                ) : null}
                {/* {selectedTech == "rokuTv" ? (
                          <Button
                            style={{ marginLeft: "10px" }}
                            size="small"
                            variant="outlined"
                            // href="https://s3.amazonaws.com/dev.cuvo.io/dist/feedback-agent/tv/roku/cuvo-sdk.pkg"
                            onClick={(event: any) =>
                              window.location.href= `${window.location.origin}/dist/feedback-agent/tv/roku/cuvo-sdk.pkg`
                              
                            }
                          >
                            <Download />
                            <span>Download SDK</span>
                          </Button>
                        ) : null} */}

                {selectedTech == "html5Tv" ? (
                  <Button
                    style={{ marginLeft: "10px" }}
                    size="small"
                    variant="outlined"
                    // href="#"
                    // target="_blank"
                    onClick={(event: any) =>
                      window.open(
                        `${window.location.origin}/docs/installation-guide/tv/hostedwebapp/CuVo-FireTV-Hosted-Webapp-Agent-Integration-Document.pdf`,
                        "_blank"
                      )
                    }
                  >
                    <OpenInNew />
                    <span>Go To Integration Doc</span>
                  </Button>
                ) : null}

                {productParams?.products?.[0]?.platform ==
                PLATFORM_TYPE_BROWSER ? (
                  <Button
                    style={{ marginLeft: "10px" }}
                    size="small"
                    variant="outlined"
                    // href="#"
                    // target="_blank"
                    onClick={(event: any) =>
                      window.open(
                        `${window.location.origin}/docs/installation-guide/browser/CuVo-Browser-Agent-Integration-Document.pdf`,
                        "_blank"
                      )
                    }
                  >
                    <OpenInNew />
                    <span>Go To Integration Doc</span>
                  </Button>
                ) : null}

                {selectedTech == "rokuTv" ? (
                  <>
                    <Grid item xs={12} sx={{ mt: 2, mb: 1 }}>
                      <FormControl
                        className="custom-select custom-textarea"
                        fullWidth
                      >
                        <InputLabel>Your package url</InputLabel>
                        <OutlinedInput
                          className="custom-code-input"
                          type="text"
                          value={`${window.location.origin}/dist/feedback-agent/tv/roku/cuvo-sdk.pkg
                                  `}
                          disabled
                          id={"packageUrlField"}
                          name={"packageUrlField"}
                          label="packageUrlField"
                          multiline
                          maxRows={10}
                          readOnly={true}
                          endAdornment={
                            <Tooltip title="Copy to Clipboard" placement="top">
                              {/* <Button
                                            className="copy-icon"
                                            onClick={(event: any) => copyToClipboard('codeTextField')}
                                          >
                                            Copy
                                          </Button> */}

                              <ContentCopyIcon
                                fontSize="small"
                                className="copy-icon"
                                onClick={(event: any) =>
                                  copyToClipboard("packageUrlField")
                                }
                              />
                            </Tooltip>
                          }
                        />
                      </FormControl>
                    </Grid>
                  </>
                ) : null}
              </Grid>
            )}
            {productParams?.products?.[0]?.apiKey && (
              <Grid item xs={12}>
                <FormControl
                  className="custom-select custom-textarea"
                  fullWidth
                >
                  <InputLabel>Code snippet</InputLabel>
                  <OutlinedInput
                    className="custom-code-input guideTourCopyIntegrationCode"
                    type="text"
                    value={
                      productParams &&
                      productParams.products &&
                      productParams.products[0] &&
                      productParams.products[0].apiKey
                        ? codeSnippet
                            .replace(
                              "YOUR_PRODUCT_API_KEY_GOES_HERE",
                              productParams.products[0].apiKey
                            )
                            .replace(
                              "YOUR_ORGID_GOES_HERE",
                              productParams.products[0].orgId
                            )
                            .replace(
                              "YOUR_PRODUCT_ID_GOES_HERE",
                              productParams.products[0].id
                            )
                            .replace(
                              "YOUR_PRODUCT_VERSION_GOES_HERE",
                              productParams.products[0].version
                            )
                        : ""
                    }
                    disabled
                    id={"codeTextField"}
                    name={"codeTextField"}
                    label="codeTextField"
                    multiline
                    maxRows={10}
                    readOnly={true}
                    endAdornment={
                      <Tooltip title="Copy to Clipboard" placement="top">
                        {/* <Button
                        className="copy-icon"
                        onClick={(event: any) => copyToClipboard('codeTextField')}
                      >
                        Copy
                      </Button> */}

                        <ContentCopyIcon
                          className="copy-icon"
                          onClick={(event: any) =>
                            copyToClipboard("codeTextField")
                          }
                        />
                      </Tooltip>
                    }
                  />
                </FormControl>
              </Grid>
            )}

            {selectedTech == "reactNative" ||
            selectedTech == "reactNativeTv" ? (
              <>
                <Typography className="label" sx={{ mt: 2, mb: 1, ml: 2 }}>
                  {"Add below code in .npmrc file"}
                </Typography>
                <Grid item xs={12}>
                  <FormControl
                    className="custom-select custom-textarea"
                    fullWidth
                  >
                    <InputLabel>Code snippet</InputLabel>
                    <OutlinedInput
                      className="custom-code-input"
                      type="text"
                      value={
                        productParams &&
                        productParams.products &&
                        productParams.products[0] &&
                        productParams.products[0].apiKey
                          ? npmCode
                          : ""
                      }
                      disabled
                      id={"codeTextField"}
                      name={"codeTextField"}
                      label="codeTextField"
                      multiline
                      maxRows={10}
                      readOnly={true}
                      endAdornment={
                        <Tooltip title="Copy to Clipboard" placement="top">
                          {/* <Button
                                  className="copy-icon"
                                  onClick={(event: any) => copyToClipboard('codeTextField')}
                                >
                                  Copy
                                </Button> */}

                          <ContentCopyIcon
                            className="copy-icon"
                            onClick={(event: any) =>
                              copyToClipboard("codeTextField")
                            }
                          />
                        </Tooltip>
                      }
                    />
                  </FormControl>
                </Grid>
              </>
            ) : null}
          </Grid>
          <Grid
            item
            sx={{ mt: 2, p: 0, mb: 0, lineHeight: 0 }}
            xs={12}
            md={12}
            className="acc-prod-footer"
          >
            <Tooltip title={renderPreviuosBtnToolTip()} placement="bottom">
              <Button
                size="small"
                className="backbtn"
                onClick={(e: any) => {
                  handleAccordianChange(true, "basicDetails", "back");
                }}
              >
                {" "}
                &nbsp;
                <KeyboardArrowLeft />
                <span className="acc-prod-prev-span">
                  Previous :
                </span>&nbsp; <span>{renderPreviuosBtnToolTip()}</span>
              </Button>
            </Tooltip>
            <>
              {" "}
              <div></div>
            </>
            {/* <Tooltip
              title="Go to Integration Steps"
              placement="bottom"
            >
              <Button
                size="small"
                className="nextbtn"
                onClick={(e: any) => {
                  handleAccordianChange(true, "basicDetails", "next");
                }}
              >
                Next
                <KeyboardArrowRight />
              </Button>
            </Tooltip>{" "} */}
          </Grid>{" "}
        </AccordionDetails>
      </Accordion>
    </>
  );
}

export default withSnackbar(APISettings);
